/**
 * Single catalog of Accounting sub-views.
 *
 * The side menu, the page header, the sibling rail and the desk map used to each
 * list the tools by hand. Names drifted (two entries called "Documentos") and
 * the work order lived only as a tooltip. This file is the source; `?view=` ids
 * stay exactly the ones already bookmarked and used by tours.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Calculator,
  CreditCard,
  FileArchive,
  FileCheck,
  FileText,
  HandCoins,
  Landmark,
  Mail,
  Package,
  Receipt,
  Scale,
  ShieldAlert,
  Wallet,
} from 'lucide-react';
import { ROUTES } from '../routes';

export const ACCOUNTING_VIEW_IDS = [
  'logistrade',
  'correoFacturas',
  'porFacturar',
  'facturasOperador',
  'revisionDocumentos',
  'siigo',
  'conciliacionSiigo',
  'pagos',
  'recaudo',
  'saldos',
  'ajustesPeso',
  'cuentasPorPagar',
  'companyDocuments',
] as const;

export type AccountingViewId = (typeof ACCOUNTING_VIEW_IDS)[number];
export type AccountingNavGroupId = 'lookup' | 'queues' | 'electronic' | 'treasury';

export interface AccountingNavItemDef {
  view: AccountingViewId;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

export interface AccountingNavGroupDef {
  id: AccountingNavGroupId;
  groupLabelKey: string;
  items: AccountingNavItemDef[];
}

function item(
  view: AccountingViewId,
  slug: string,
  icon: LucideIcon,
): AccountingNavItemDef {
  return {
    view,
    icon,
    labelKey: `accounting.nav.items.${slug}.label`,
    descriptionKey: `accounting.nav.items.${slug}.description`,
  };
}

export const ACCOUNTING_NAV_GROUPS: AccountingNavGroupDef[] = [
  {
    id: 'lookup',
    groupLabelKey: 'accounting.nav.groups.lookup',
    items: [
      item('logistrade', 'logistrade', Calculator),
    ],
  },
  {
    id: 'queues',
    groupLabelKey: 'accounting.nav.groups.queues',
    items: [
      item('correoFacturas', 'invoiceEmail', Mail),
      item('porFacturar', 'pendingInvoice', FileCheck),
      item('facturasOperador', 'operatorInvoices', Receipt),
      item('revisionDocumentos', 'documentReview', ShieldAlert),
    ],
  },
  {
    id: 'electronic',
    groupLabelKey: 'accounting.nav.groups.electronic',
    items: [
      item('siigo', 'siigo', FileText),
      item('conciliacionSiigo', 'siigoReconciliation', Scale),
    ],
  },
  {
    id: 'treasury',
    groupLabelKey: 'accounting.nav.groups.treasury',
    items: [
      item('pagos', 'payments', CreditCard),
      item('recaudo', 'collections', Landmark),
      item('saldos', 'balances', Wallet),
      item('ajustesPeso', 'weightAdjustments', Package),
      item('cuentasPorPagar', 'accountsPayable', HandCoins),
      item('companyDocuments', 'companyDocuments', FileArchive),
    ],
  },
];

export function accountingPath(view: AccountingViewId): string {
  return `${ROUTES.accounting}?view=${view}`;
}

export function flattenAccountingNav() {
  return ACCOUNTING_NAV_GROUPS.flatMap((group) =>
    group.items.map((navItem) => ({ ...navItem, groupId: group.id, groupLabelKey: group.groupLabelKey })),
  );
}

export function accountingGroupFor(view: string): AccountingNavGroupDef | undefined {
  return ACCOUNTING_NAV_GROUPS.find((group) => group.items.some((navItem) => navItem.view === view));
}

export function accountingItemFor(view: string): AccountingNavItemDef | undefined {
  return flattenAccountingNav().find((navItem) => navItem.view === view);
}

export function isAccountingView(value: string): value is AccountingViewId {
  return (ACCOUNTING_VIEW_IDS as readonly string[]).includes(value);
}

/** Sidebar entries: same order, group marker only on the first item of each group. */
export function accountingSidebarSubItems() {
  return ACCOUNTING_NAV_GROUPS.flatMap((group) =>
    group.items.map((navItem, index) => ({
      id: accountingPath(navItem.view),
      labelKey: navItem.labelKey,
      icon: navItem.icon,
      descriptionKey: navItem.descriptionKey,
      ...(index === 0 ? { groupLabelKey: group.groupLabelKey } : {}),
    })),
  );
}
